import type {AiMessage} from '../services/ai.service';

export interface ConversationUpdatePage {
  updates: AiMessage[];
  nextCursor: string;
  hasMore: boolean;
  pendingTasks: number;
}

export interface ConversationUpdateIdentity {
  conversationId: string;
  organizationId: string;
  userId: string;
}

export function mergeConversationUpdates(messages: AiMessage[], updates: AiMessage[]): AiMessage[] {
  const merged = new Map(messages.map(message => [message.id, message]));
  for (const update of updates) {
    if (!merged.has(update.id)) merged.set(update.id, update);
  }
  return [...merged.values()].sort((left, right) =>
    Date.parse(left.createdAt) - Date.parse(right.createdAt));
}

export class ConversationUpdatePoller {
  private timer?: ReturnType<typeof setTimeout>;
  private generation = 0;
  private identityKey = '';
  private cursor = '0';
  private activeGeneration?: number;

  constructor(
    private identity: () => ConversationUpdateIdentity | null,
    private canDisplay: () => boolean,
    private read: (conversationId: string, cursor: string) => Promise<ConversationUpdatePage>,
    private deliver: (updates: AiMessage[]) => void,
  ) {}

  stop() {
    clearTimeout(this.timer);
    this.generation++;
    this.identityKey = '';
    this.cursor = '0';
    this.activeGeneration = undefined;
  }

  async refresh(initialLoad = false): Promise<void> {
    clearTimeout(this.timer);
    const identity = this.identity();
    if (!identity) {
      this.stop();
      return;
    }
    const key = JSON.stringify(identity);
    if (key !== this.identityKey) {
      this.stop();
      this.identityKey = key;
    }
    const generation = this.generation;
    if (this.activeGeneration === generation) return;
    if (!initialLoad && !this.canDisplay()) {
      this.schedule(generation);
      return;
    }
    this.activeGeneration = generation;
    try {
      // Initial history stays hidden until its saved completion updates are ready.
      // Drain pagination immediately instead of revealing another page every second.
      let pages = 0;
      let page: ConversationUpdatePage;
      do {
        page = await this.read(identity.conversationId, this.cursor);
        if (generation !== this.generation || JSON.stringify(this.identity()) !== key) return;
        if (!initialLoad && !this.canDisplay()) {
          this.schedule(generation);
          return;
        }
        this.deliver(page.updates);
        const previousCursor = this.cursor;
        this.cursor = page.nextCursor;
        if (this.cursor === previousCursor && page.hasMore) break;
      } while (initialLoad && page.hasMore && ++pages < 10);
      if (page.hasMore || page.pendingTasks > 0) this.schedule(generation, page.hasMore ? 1000 : 5000);
    } catch (error) {
      const status = typeof error === 'object' && error !== null && 'status' in error ? error.status : 0;
      if (generation === this.generation && ![401, 403, 404].includes(Number(status))) {
        this.schedule(generation);
      }
      if (initialLoad) throw error;
    } finally {
      if (this.activeGeneration === generation) this.activeGeneration = undefined;
    }
  }

  private schedule(generation: number, delay = 5000) {
    if (generation !== this.generation) return;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.refresh(), delay);
  }
}
